import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { goodsApi } from "@/api";
import { auth } from "@/lib/auth";
import { buildLoginRedirect } from "@/lib/navigation";
import type { GoodsDetailResult } from "@/api/modules/goods";

type TabKey = "detail" | "notice" | "reviews";

const DEFAULT_GOODS_ID = 80;
const DEFAULT_COACH_ID = 115;

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: "detail", label: "项目详情" },
  { key: "notice", label: "下单须知" },
  { key: "reviews", label: "用户评价" },
];

export default function ServiceDetailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const goodsId = Number(searchParams.get("id") || DEFAULT_GOODS_ID);
  const coachId = Number(searchParams.get("coachId") || DEFAULT_COACH_ID);
  const [activeTab, setActiveTab] = useState<TabKey>("detail");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState<GoodsDetailResult | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadDetail = async () => {
      setLoading(true);
      setError("");
      try {
        const result = await goodsApi.getGoodsDetail(goodsId);
        if (!cancelled) {
          setDetail(result);
        }
      } catch (requestError) {
        if (!cancelled) {
          setError(requestError instanceof Error ? requestError.message : "服务详情加载失败");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadDetail();

    return () => {
      cancelled = true;
    };
  }, [goodsId]);

  const gallery = useMemo(
    () => (detail?.goods_image?.length ? detail.goods_image : detail?.image ? [detail.image] : []),
    [detail],
  );

  const handleOrder = () => {
    const target = `/order-confirm?goodsId=${goodsId}&coachId=${coachId}&qty=1`;
    if (!auth.getToken()) {
      navigate(buildLoginRedirect(target));
      return;
    }
    navigate(target);
  };

  return (
    <div className="flex min-h-screen flex-col" style={{ background: "#F4F4F6", maxWidth: 480, margin: "0 auto" }}>
      <div className="relative h-64 w-full">
        <img
          src={
            gallery[0] ||
            "https://readdy.ai/api/search-image?query=spa%20service%20banner%20massage%20warm%20light%20clean%20towels&width=480&height=320&seq=service-detail-fallback&orientation=landscape"
          }
          alt={detail?.name || "服务图片"}
          className="h-full w-full object-cover object-top"
        />
        <button
          type="button"
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))}
          className="absolute left-4 top-4 flex h-9 w-9 items-center justify-center rounded-full"
          style={{ background: "rgba(0,0,0,0.3)" }}
        >
          <i className="ri-arrow-left-line text-xl text-white" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto pb-28">
        {error ? (
          <div
            className="mx-4 mt-4 rounded-2xl px-4 py-3 text-sm"
            style={{ background: "#FFF4EC", color: "#D94A00", border: "1px solid #FFD8BF" }}
          >
            {error}
          </div>
        ) : null}

        <div className="bg-white px-4 pb-4 pt-4">
          <h1 className="mb-2 text-base font-bold text-[#1A1A1A]">{loading ? "加载中..." : detail?.name || "服务详情"}</h1>
          <div className="mb-3 flex items-center gap-4">
            <span className="text-lg font-bold text-[#FF5C00]">￥{detail?.price || 0}</span>
            {detail?.scribing_price ? <span className="text-sm text-[#C0C0C0] line-through">￥{detail.scribing_price}</span> : null}
            <span className="text-sm text-[#AAAAAA]">{detail?.duration || 0}分钟</span>
            <span className="text-sm text-[#AAAAAA]">销量 {detail?.total_order_num || 0}</span>
          </div>

          <div className="rounded-xl px-3 py-3" style={{ background: "#F9F9F9", border: "1px solid #F2F2F2" }}>
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#888]">服务保障</span>
              <span className="text-sm font-medium text-[#333]">平台认证技师，支持真实下单</span>
            </div>
          </div>
        </div>

        <div className="sticky top-0 z-30 mt-2 bg-white">
          <div className="flex">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className="flex-1 py-3.5 text-sm font-semibold"
                style={{
                  color: activeTab === tab.key ? "#FF7B22" : "#888",
                  borderBottom: activeTab === tab.key ? "2px solid #FF7B22" : "2px solid transparent",
                  background: activeTab === tab.key ? "#FFFAF6" : "#fff",
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {activeTab === "detail" ? (
          <div className="mt-2 bg-white px-4 py-4">
            <h3 className="mb-3 text-sm font-bold text-[#1A1A1A]">项目介绍</h3>
            {detail?.tags ? (
              <div className="mb-4 flex flex-wrap gap-2">
                {detail.tags.split("|").filter(Boolean).map((tag) => (
                  <span key={tag} className="rounded-full bg-[#FFF3EC] px-3 py-1 text-xs text-[#FF7B22]">
                    {tag}
                  </span>
                ))}
              </div>
            ) : null}
            <div
              className="prose prose-sm max-w-none text-[#666]"
              dangerouslySetInnerHTML={{ __html: detail?.content || "<p>暂无详情说明</p>" }}
            />
          </div>
        ) : null}

        {activeTab === "notice" ? (
          <div className="mt-2 bg-white px-4 py-4">
            {[
              "下单前请确认服务地址在可服务范围内。",
              "订单提交后请保持电话畅通，便于技师联系。",
              "服务开始前 2 小时可免费取消。",
              "实际出行费以结算页测算结果为准。",
            ].map((text) => (
              <div key={text} className="mb-3 flex items-start gap-3">
                <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-full bg-[#FFF3EC]">
                  <i className="ri-information-line text-sm text-[#FF7B22]" />
                </div>
                <p className="flex-1 text-sm leading-6 text-[#666]">{text}</p>
              </div>
            ))}
          </div>
        ) : null}

        {activeTab === "reviews" ? (
          <div className="mt-2 bg-white px-4 py-4">
            <h3 className="mb-4 text-sm font-bold text-[#1A1A1A]">用户评价 ({detail?.comment_count || 0})</h3>
            {detail?.comment_lists?.length ? (
              <div className="space-y-4">
                {detail.comment_lists.map((comment, index) => (
                  <div key={comment.id}>
                    <div className="mb-2 flex items-center gap-2">
                      <div className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-[#F0F0F0]">
                        {comment.user?.avatar ? (
                          <img src={comment.user.avatar} alt={comment.user.nickname || "用户"} className="h-full w-full object-cover" />
                        ) : (
                          <i className="ri-user-line text-sm text-[#CCC]" />
                        )}
                      </div>
                      <span className="flex-1 text-xs font-semibold text-[#1A1A1A]">{comment.user?.nickname || "匿名用户"}</span>
                      <span className="text-xs text-[#CCCCCC]">{comment.create_time || ""}</span>
                    </div>
                    <div className="mb-2 flex gap-0.5 pl-11">
                      {Array.from({ length: 5 }).map((_, starIndex) => (
                        <i
                          key={starIndex}
                          className={starIndex < (comment.service_comment || 0) ? "ri-star-fill" : "ri-star-line"}
                          style={{ fontSize: 12, color: starIndex < (comment.service_comment || 0) ? "#FFAA00" : "#DDD" }}
                        />
                      ))}
                    </div>
                    <p className="pl-11 text-sm leading-6 text-[#666]">{comment.comment || "用户未填写评价内容"}</p>
                    {index < detail.comment_lists.length - 1 ? <div className="mt-4 border-b border-[#F5F5F5]" /> : null}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[#AAAAAA]">暂无评价</p>
            )}
          </div>
        ) : null}
      </div>

      <div
        className="fixed bottom-0 w-full bg-white px-4 py-3"
        style={{ maxWidth: 480, left: "50%", transform: "translateX(-50%)", borderTop: "1px solid #F0F0F0" }}
      >
        <button
          type="button"
          onClick={handleOrder}
          className="w-full rounded-full py-3.5 text-sm font-bold text-white"
          style={{ background: "linear-gradient(90deg, #FF8C3A, #FF5C00)" }}
        >
          立即下单
        </button>
      </div>
    </div>
  );
}
